//deps
import { useContext, useState, useCallback } from "react";

//api
import { delinkWallet } from "@payment-client/apis/Wallet";
import { delinkCorporateWallet, delinkWalletV3 } from "@payment-client/apis/publicWallet";

//internal deps
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    getBridgeSdk,
    PublicApiContext,
} from "@swiggy-private/checkout-core";
import { captureError, useMountedRef } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { LinkedWalletDetails } from "@swiggy-private/payment-ui";

//defs
import { AMAZON_PAY_DELINK_STATUS } from "@swiggy-private/native-bridge";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

// helpers
import { generateCorporateDelinkRequestBody, getCorporatePartnerId } from "@payment-client/helpers/Corporate";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { isEmpty } from "@swiggy-private/js-utils";

interface UseDelinkWalletReturnType {
    isDelinkInProgress: boolean;
    isDelinkSuccess: boolean;
    isDelinkFailure: boolean;
    delinkUserWallet: (walletCode: WALLET_CODES) => void;
    resetDelinkState: VoidFunction;
}

enum DelinkState {
    SUCCESS,
    FAILURE,
    LOADING,
    NONE,
}

export const useDelinkWallet = (
    walletDetails: LinkedWalletDetails | undefined,
    tenant?: string,
): UseDelinkWalletReturnType => {
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const isMountedRef = useMountedRef();
    const publicApiConfig = useContext(PublicApiContext);

    const [delinkState, setDelinkState] = useState<DelinkState>(DelinkState.NONE);

    const resetDelinkState = useCallback(() => setDelinkState(DelinkState.NONE), []);

    const delinkAmazonPay = useCallback(() => {
        getBridgeSdk()
            .delinkAmazonPay({ ...(!isEmpty(tenant) && { tenant: tenant as Tenant }) })
            .then((amazonPayDelinkResponse) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (amazonPayDelinkResponse?.status === AMAZON_PAY_DELINK_STATUS.SUCCESS) {
                    setDelinkState(DelinkState.SUCCESS);
                } else {
                    setDelinkState(DelinkState.FAILURE);
                }
            })
            .catch(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setDelinkState(DelinkState.FAILURE);
            });
    }, [isMountedRef, tenant]);

    const delinkUserWallet = useCallback(
        async (walletCode: WALLET_CODES) => {
            if (delinkState === DelinkState.LOADING || !isMountedRef.current) {
                return;
            }

            setDelinkState(DelinkState.LOADING);
            try {
                const isPublicApiEnable = isPublicApiEnabled(
                    publicApiConfig || {},
                    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.WALLET_APIS,
                    walletCode.toLowerCase(),
                );

                const delinkWalletFunction = isPublicApiEnable ? delinkWalletV3 : delinkWallet;

                switch (walletCode.toLowerCase()) {
                    case WALLET_CODES.amazonpay.toLowerCase():
                        await delinkWalletV3(DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST, walletCode);
                        delinkAmazonPay();
                        break;
                    case WALLET_CODES.mmt.toLowerCase(): {
                        const partnerId = getCorporatePartnerId(walletDetails?.corporateWalletDetails);
                        const reqBody = generateCorporateDelinkRequestBody(partnerId);

                        await delinkCorporateWallet(reqBody);
                        setDelinkState(DelinkState.SUCCESS);
                        break;
                    }
                    default:
                        await delinkWalletFunction(apiHost, walletCode);
                        setDelinkState(DelinkState.SUCCESS);
                }
            } catch (err) {
                captureError(err);
                setDelinkState(DelinkState.FAILURE);
            }
        },
        [apiHost, delinkAmazonPay, delinkState, isMountedRef, publicApiConfig, walletDetails?.corporateWalletDetails],
    );

    return {
        isDelinkInProgress: delinkState === DelinkState.LOADING,
        isDelinkSuccess: delinkState === DelinkState.SUCCESS,
        isDelinkFailure: delinkState === DelinkState.FAILURE,
        delinkUserWallet,
        resetDelinkState,
    };
};
